// src/components/Admin/AdminQRManagement.jsx
import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where, doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { QRCodeCanvas } from 'qrcode.react';
import logoImg from '../../assets/logo.png';

/**
 * Componente per la gestione dei QR code e account degli utenti da parte dell'amministratore
 */
const AdminQRManagement = () => {
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [updating, setUpdating] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  const [logoLoaded, setLogoLoaded] = useState(false);
  const [logoUrl, setLogoUrl] = useState('');
  const [showInactive, setShowInactive] = useState(false); // Nuovo stato per mostrare/nascondere account disabilitati
  const [confirmDialog, setConfirmDialog] = useState({ show: false, userId: null, userName: '', action: '' });

  // Carica il logo
  useEffect(() => {
    const img = new Image();
    img.src = logoImg;
    img.onload = () => {
      setLogoLoaded(true);
      // Crea una data URL dal logo
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      setLogoUrl(canvas.toDataURL());
    };
    img.onerror = (error) => {
      console.error('Errore nel caricamento del logo:', error);
      generateDefaultLogo();
    };
  }, []);

  // Genera un logo predefinito in caso di errore
  const generateDefaultLogo = () => {
    try {
      const canvas = document.createElement('canvas');
      const size = 100;
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');
      
      // Disegna un cerchio con sfondo bianco
      ctx.fillStyle = '#FFFFFF';
      ctx.beginPath();
      ctx.arc(size/2, size/2, size/2, 0, 2 * Math.PI);
      ctx.fill();
      
      // Disegna il bordo
      ctx.strokeStyle = '#3498db';
      ctx.lineWidth = 6;
      ctx.beginPath();
      ctx.arc(size/2, size/2, size/2 - 3, 0, 2 * Math.PI);
      ctx.stroke();
      
      // Disegna le lettere "IC" (per Iacuzzo Construction)
      ctx.fillStyle = '#2c3e50';
      ctx.font = 'bold 40px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('IC', size/2, size/2);
      
      // Imposta il logo generato
      setLogoUrl(canvas.toDataURL());
      setLogoLoaded(true);
    } catch (error) {
      console.error('Errore nella generazione del logo:', error);
    }
  };

  // Carica gli utenti
  useEffect(() => {
    const fetchUsers = async () => {
      setIsLoading(true);
      try {
        // Ottieni tutti gli utenti non-admin
        const usersQuery = query(collection(db, "users"), where("role", "!=", "admin"));
        const querySnapshot = await getDocs(usersQuery);
        
        const usersData = [];
        
        // Per ogni utente, ottieni anche lo stato QR code e stato account
        for (const userDoc of querySnapshot.docs) {
          const userData = userDoc.data();
          
          // Verifica se l'utente ha una configurazione qrStatus
          let qrStatus = { active: true }; // Default: QR attivo
          
          if (userData.qrStatus) {
            qrStatus = userData.qrStatus;
          }

          // Verifica lo stato dell'account
          const accountStatus = userData.accountStatus || { active: true };
          
          usersData.push({
            id: userDoc.id,
            email: userData.email,
            nome: userData.nome || '',
            cognome: userData.cognome || '',
            qrStatus: qrStatus,
            accountStatus: accountStatus,
            lastLogin: userData.lastLogin || null,
            createdAt: userData.createdAt || null
          });
        }
        
        setUsers(usersData);
        setError(null);
      } catch (err) {
        console.error("Errore nel caricamento degli utenti:", err);
        setError("Impossibile caricare gli utenti. Riprova più tardi.");
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchUsers();
  }, []);

  // Filtra gli utenti in base al termine di ricerca e stato account
  useEffect(() => {
    let filtered = users;

    // Filtra per stato account
    if (!showInactive) {
      filtered = filtered.filter(user => user.accountStatus?.active !== false);
    }

    // Filtra per termine di ricerca
    if (searchTerm.trim() !== '') {
      const lowercasedSearch = searchTerm.toLowerCase();
      filtered = filtered.filter(user => 
        user.email.toLowerCase().includes(lowercasedSearch) ||
        user.nome.toLowerCase().includes(lowercasedSearch) ||
        user.cognome.toLowerCase().includes(lowercasedSearch)
      );
    }

    setFilteredUsers(filtered);
  }, [searchTerm, users, showInactive]);

  // Cambia lo stato del QR code (attivo/disattivo)
  const toggleQRStatus = async (userId, currentStatus) => {
    setUpdating(true);
    try {
      const newStatus = !currentStatus;
      const userRef = doc(db, "users", userId);
      
      // Aggiorna lo stato nel database
      await updateDoc(userRef, {
        "qrStatus.active": newStatus,
        "qrStatus.lastUpdated": new Date(),
        "qrStatus.reason": newStatus ? "" : "Disabilitato dall'amministratore"
      });
      
      // Aggiorna lo stato locale
      setUsers(prevUsers => 
        prevUsers.map(user => 
          user.id === userId 
            ? { ...user, qrStatus: { ...user.qrStatus, active: newStatus } } 
            : user
        )
      );
      
      // Mostra notifica
      setNotification({
        show: true,
        message: `QR code ${newStatus ? 'attivato' : 'disattivato'} con successo`,
        type: 'success'
      });
      
      // Nascondi la notifica dopo 3 secondi
      setTimeout(() => {
        setNotification(prev => ({ ...prev, show: false }));
      }, 3000);
    } catch (error) {
      console.error("Errore nell'aggiornamento dello stato QR code:", error);
      setNotification({
        show: true,
        message: `Errore: ${error.message}`,
        type: 'error'
      });
    } finally {
      setUpdating(false);
    }
  };

  // Cambia lo stato dell'account (attivo/disattivo)
  const toggleAccountStatus = async (userId, currentStatus) => {
    setConfirmDialog({ show: false, userId: null, userName: '', action: '' });
    setUpdating(true);
    
    try {
      const newStatus = !currentStatus;
      const userRef = doc(db, "users", userId);
      
      // Aggiorna lo stato nel database
      await updateDoc(userRef, {
        "accountStatus.active": newStatus,
        "accountStatus.lastUpdated": new Date(),
        "accountStatus.reason": newStatus ? "Riattivato dall'amministratore" : "Account disabilitato - Dipendente non più in forza",
        "accountStatus.disabledBy": newStatus ? null : "admin",
        "accountStatus.disabledDate": newStatus ? null : new Date(),
        // Se disabilito l'account, disabilito anche il QR
        ...(newStatus === false && {
          "qrStatus.active": false,
          "qrStatus.lastUpdated": new Date(),
          "qrStatus.reason": "QR disabilitato per inattività account"
        })
      });
      
      // Aggiorna lo stato locale
      setUsers(prevUsers => 
        prevUsers.map(user => 
          user.id === userId 
            ? { 
                ...user, 
                accountStatus: { ...user.accountStatus, active: newStatus },
                // Se disabilito l'account, disabilito anche il QR localmente
                qrStatus: newStatus === false ? { ...user.qrStatus, active: false } : user.qrStatus
              } 
            : user
        )
      );
      
      // Mostra notifica
      setNotification({
        show: true,
        message: `Account ${newStatus ? 'riattivato' : 'disabilitato'} con successo${!newStatus ? '. L\'utente non potrà più accedere al sistema.' : '.'}`,
        type: 'success'
      });
      
      // Nascondi la notifica dopo 3 secondi
      setTimeout(() => {
        setNotification(prev => ({ ...prev, show: false }));
      }, 3000);
    } catch (error) {
      console.error("Errore nell'aggiornamento dello stato account:", error);
      setNotification({
        show: true,
        message: `Errore: ${error.message}`,
        type: 'error'
      });
    } finally {
      setUpdating(false);
    }
  };

  // Mostra dialogo di conferma
  const showConfirmDialog = (userId, userName, action) => {
    setConfirmDialog({
      show: true,
      userId: userId,
      userName: userName,
      action: action
    });
  };

  // Genera un valore QR code per l'anteprima
  const getQRValue = (userId) => {
    return `iacuzzo:user:${userId}:preview`;
  };

  // Scarica il QR code di un utente
  const downloadQRCode = (userId, userName) => {
    const qrElement = document.getElementById(`qr-${userId}`);
    if (!qrElement) return;
    
    try {
      // Trova il canvas nel QR code
      const canvas = qrElement.querySelector('canvas');
      if (!canvas) {
        console.error('Canvas del QR code non trovato');
        return;
      }
      
      // Crea un link per il download
      const link = document.createElement('a');
      link.href = canvas.toDataURL('image/png');
      link.download = `qrcode-${userName.replace(/\s+/g, '-')}.png`;
      
      // Simula il click sul link per avviare il download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Errore nel download del QR code:', error);
      alert('Si è verificato un errore durante il download del QR code.');
    }
  };

  // Formatta la data
  const formatDate = (date) => {
    if (!date) return 'Mai';
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString('it-IT', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="admin-qr-management">
      <h3>Gestione Account e QR Code Utenti</h3>
      
      {/* Dialogo di conferma */}
      {confirmDialog.show && (
        <div className="confirm-dialog-overlay">
          <div className="confirm-dialog">
            <h4>Conferma {confirmDialog.action === 'disable' ? 'Disabilitazione' : 'Riattivazione'} Account</h4>
            <p>
              Sei sicuro di voler {confirmDialog.action === 'disable' ? 'disabilitare' : 'riattivare'} l'account di <strong>{confirmDialog.userName}</strong>?
            </p>
            {confirmDialog.action === 'disable' && (
              <p className="warning-text">
                ⚠️ L'utente non potrà più accedere al sistema e verrà escluso da tutte le sezioni.
                Il QR code verrà automaticamente disabilitato.
              </p>
            )}
            <div className="dialog-buttons">
              <button 
                className={`btn ${confirmDialog.action === 'disable' ? 'btn-danger' : 'btn-success'}`}
                onClick={() => {
                  const user = users.find(u => u.id === confirmDialog.userId);
                  toggleAccountStatus(confirmDialog.userId, user.accountStatus?.active !== false);
                }}
              >
                {confirmDialog.action === 'disable' ? 'Disabilita' : 'Riattiva'}
              </button>
              <button 
                className="btn btn-secondary"
                onClick={() => setConfirmDialog({ show: false, userId: null, userName: '', action: '' })}
              >
                Annulla
              </button>
            </div>
          </div>
        </div>
      )}
      
      {notification.show && (
        <div className={`notification ${notification.type}`}>
          {notification.message}
          <button className="close-notification" onClick={() => setNotification(prev => ({ ...prev, show: false }))}>×</button>
        </div>
      )}
      
      <div className="controls-bar">
        <div className="search-section">
          <input
            type="text"
            placeholder="Cerca utente per nome o email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        
        <div className="filters-section">
          <label className="toggle-inactive">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
            />
            <span>Mostra account disabilitati</span>
          </label>
        </div>
        
        <div className="stats-section">
          <div className="total-users">
            Utenti attivi: <strong>{users.filter(u => u.accountStatus?.active !== false).length}</strong>
            {showInactive && (
              <span className="inactive-count">
                {' '}| Disabilitati: <strong>{users.filter(u => u.accountStatus?.active === false).length}</strong>
              </span>
            )}
          </div>
        </div>
      </div>
      
      {isLoading ? (
        <div className="loading">Caricamento utenti...</div>
      ) : error ? (
        <div className="error-message">{error}</div>
      ) : filteredUsers.length === 0 ? (
        <div className="no-results">Nessun utente trovato</div>
      ) : (
        <div className="users-grid">
          {filteredUsers.map(user => {
            const userName = user.nome && user.cognome ? `${user.nome} ${user.cognome}` : user.email;
            const isQRActive = user.qrStatus?.active !== false; // Default a true se non specificato
            const isAccountActive = user.accountStatus?.active !== false; // Default a true se non specificato
            
            return (
              <div key={user.id} className={`user-qr-card ${isAccountActive ? '' : 'account-disabled'} ${isQRActive ? '' : 'qr-disabled'}`}>
                {/* Badge stato account */}
                {!isAccountActive && (
                  <div className="account-disabled-badge">
                    ACCOUNT DISABILITATO
                  </div>
                )}
                
                <div className="user-info">
                  <h4>{userName}</h4>
                  <p className="user-email">{user.email}</p>
                  {user.lastLogin && (
                    <p className="last-login">Ultimo accesso: {formatDate(user.lastLogin)}</p>
                  )}
                  {!isAccountActive && user.accountStatus?.disabledDate && (
                    <p className="disabled-date">Disabilitato il: {formatDate(user.accountStatus.disabledDate)}</p>
                  )}
                </div>
                
                <div className="qr-container" id={`qr-${user.id}`}>
                  {!isAccountActive ? (
                    <>
                      <div className="account-disabled-overlay">
                        <div className="disabled-text">ACCOUNT<br/>DISABILITATO</div>
                      </div>
                      <QRCodeCanvas
                        value={getQRValue(user.id)}
                        size={150}
                        level="H"
                        includeMargin={true}
                        imageSettings={logoLoaded ? {
                          src: logoUrl,
                          height: 33,
                          width: 33,
                          excavate: true
                        } : undefined}
                        style={{ opacity: 0.3 }}
                      />
                    </>
                  ) : isQRActive ? (
                    <>
                      <QRCodeCanvas
                        value={getQRValue(user.id)}
                        size={150}
                        level="H"
                        includeMargin={true}
                        imageSettings={logoLoaded ? {
                          src: logoUrl,
                          height: 33,
                          width: 33,
                          excavate: true
                        } : undefined}
                      />
                      <div className="qr-badge qr-active">QR Attivo</div>
                    </>
                  ) : (
                    <>
                      <div className="qr-disabled-overlay">
                        <div className="disabled-text">QR DISABILITATO</div>
                      </div>
                      <QRCodeCanvas
                        value={getQRValue(user.id)}
                        size={150}
                        level="H"
                        includeMargin={true}
                        imageSettings={logoLoaded ? {
                          src: logoUrl,
                          height: 33,
                          width: 33,
                          excavate: true
                        } : undefined}
                        style={{ opacity: 0.5 }}
                      />
                      <div className="qr-badge qr-inactive">QR Disattivato</div>
                    </>
                  )}
                </div>
                
                <div className="actions-container">
                  {/* Azioni QR */}
                  <div className="qr-actions">
                    <button
                      className={`btn ${isQRActive ? 'btn-warning' : 'btn-info'} btn-sm`}
                      onClick={() => toggleQRStatus(user.id, isQRActive)}
                      disabled={updating || !isAccountActive}
                      title={!isAccountActive ? 'Account disabilitato' : ''}
                    >
                      {isQRActive ? '🔒 Disattiva QR' : '🔓 Attiva QR'}
                    </button>
                    
                    <button 
                      className="btn btn-primary btn-sm"
                      onClick={() => downloadQRCode(user.id, userName)}
                      disabled={!isAccountActive}
                      title={!isAccountActive ? 'Account disabilitato' : 'Scarica QR Code'}
                    >
                      📥 Scarica QR
                    </button>
                  </div>
                  
                  {/* Azioni Account */}
                  <div className="account-actions">
                    <button
                      className={`btn ${isAccountActive ? 'btn-danger' : 'btn-success'} btn-sm btn-account`}
                      onClick={() => showConfirmDialog(
                        user.id, 
                        userName, 
                        isAccountActive ? 'disable' : 'enable'
                      )}
                      disabled={updating}
                      title={isAccountActive ? 'Disabilita account utente' : 'Riattiva account utente'}
                    >
                      {isAccountActive ? '⛔ Disabilita Account' : '✅ Riattiva Account'}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      
      <style jsx>{`
        .admin-qr-management {
          padding: 20px;
          max-width: 1400px;
          margin: 0 auto;
        }

        .admin-qr-management h3 {
          margin-bottom: 30px;
          color: #2c3e50;
          border-bottom: 2px solid #3498db;
          padding-bottom: 10px;
        }

        /* Barra dei controlli */
        .controls-bar {
          display: flex;
          flex-wrap: wrap;
          gap: 20px;
          margin-bottom: 30px;
          padding: 15px;
          background: #f8f9fa;
          border-radius: 8px;
          align-items: center;
        }

        .search-section {
          flex: 1;
          min-width: 250px;
        }

        .search-input {
          width: 100%;
          padding: 10px 15px;
          font-size: 16px;
          border: 1px solid #ddd;
          border-radius: 5px;
          transition: border-color 0.3s;
        }

        .search-input:focus {
          outline: none;
          border-color: #3498db;
        }

        .filters-section {
          display: flex;
          align-items: center;
          gap: 15px;
        }

        .toggle-inactive {
          display: flex;
          align-items: center;
          cursor: pointer;
          user-select: none;
        }

        .toggle-inactive input[type="checkbox"] {
          margin-right: 8px;
          cursor: pointer;
        }

        .toggle-inactive span {
          color: #666;
          font-size: 14px;
        }

        .stats-section {
          display: flex;
          align-items: center;
        }

        .total-users {
          color: #666;
          font-size: 14px;
        }

        .inactive-count {
          color: #dc3545;
        }

        /* Griglia utenti */
        .users-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 20px;
        }

        /* Card utente */
        .user-qr-card {
          background: white;
          border-radius: 10px;
          padding: 20px;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
          transition: all 0.3s;
          position: relative;
          overflow: hidden;
        }

        .user-qr-card:hover {
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
          transform: translateY(-2px);
        }

        .user-qr-card.account-disabled {
          background: linear-gradient(135deg, #fff 0%, #ffebeb 100%);
          opacity: 0.9;
        }

        .account-disabled-badge {
          position: absolute;
          top: 10px;
          right: -30px;
          background: #dc3545;
          color: white;
          padding: 5px 40px;
          font-size: 11px;
          font-weight: bold;
          transform: rotate(45deg);
          box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
          z-index: 10;
        }

        .user-info h4 {
          margin: 0 0 5px 0;
          color: #2c3e50;
          font-size: 18px;
        }

        .user-email {
          color: #7f8c8d;
          font-size: 14px;
          margin: 5px 0;
        }

        .last-login, .disabled-date {
          font-size: 12px;
          color: #95a5a6;
          margin: 3px 0;
        }

        .disabled-date {
          color: #dc3545;
          font-weight: 500;
        }

        /* Container QR */
        .qr-container {
          margin: 20px 0;
          text-align: center;
          position: relative;
          min-height: 180px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .qr-disabled-overlay, .account-disabled-overlay {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          z-index: 5;
          background: rgba(255, 255, 255, 0.95);
          padding: 10px 20px;
          border-radius: 5px;
          border: 2px solid #dc3545;
        }

        .account-disabled-overlay {
          background: rgba(220, 53, 69, 0.95);
          border: 2px solid #a71e2e;
        }

        .disabled-text {
          color: #dc3545;
          font-weight: bold;
          font-size: 16px;
        }

        .account-disabled-overlay .disabled-text {
          color: white;
          text-align: center;
          line-height: 1.3;
        }

        .qr-badge {
          position: absolute;
          bottom: 10px;
          left: 50%;
          transform: translateX(-50%);
          padding: 4px 12px;
          border-radius: 12px;
          font-size: 11px;
          font-weight: bold;
          text-transform: uppercase;
        }

        .qr-active {
          background: #d4edda;
          color: #155724;
        }

        .qr-inactive {
          background: #f8d7da;
          color: #721c24;
        }

        /* Container azioni */
        .actions-container {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .qr-actions, .account-actions {
          display: flex;
          gap: 10px;
          justify-content: center;
        }

        /* Bottoni */
        .btn {
          padding: 8px 15px;
          border: none;
          border-radius: 5px;
          cursor: pointer;
          font-size: 13px;
          font-weight: 500;
          transition: all 0.3s;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          text-transform: none;
        }

        .btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .btn-sm {
          padding: 6px 12px;
          font-size: 12px;
        }

        .btn-primary {
          background: #3498db;
          color: white;
        }

        .btn-primary:hover:not(:disabled) {
          background: #2980b9;
        }

        .btn-danger {
          background: #dc3545;
          color: white;
        }

        .btn-danger:hover:not(:disabled) {
          background: #c82333;
        }

        .btn-success {
          background: #28a745;
          color: white;
        }

        .btn-success:hover:not(:disabled) {
          background: #218838;
        }

        .btn-warning {
          background: #ffc107;
          color: #212529;
        }

        .btn-warning:hover:not(:disabled) {
          background: #e0a800;
        }

        .btn-info {
          background: #17a2b8;
          color: white;
        }

        .btn-info:hover:not(:disabled) {
          background: #138496;
        }

        .btn-secondary {
          background: #6c757d;
          color: white;
        }

        .btn-secondary:hover:not(:disabled) {
          background: #5a6268;
        }

        .btn-account {
          width: 100%;
          font-weight: 600;
        }

        /* Notifiche */
        .notification {
          position: fixed;
          top: 20px;
          right: 20px;
          padding: 15px 20px;
          border-radius: 5px;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
          z-index: 1000;
          display: flex;
          align-items: center;
          animation: slideIn 0.3s ease;
        }

        @keyframes slideIn {
          from {
            transform: translateX(400px);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        .notification.success {
          background: #d4edda;
          color: #155724;
          border: 1px solid #c3e6cb;
        }

        .notification.error {
          background: #f8d7da;
          color: #721c24;
          border: 1px solid #f5c6cb;
        }

        .close-notification {
          background: none;
          border: none;
          font-size: 20px;
          margin-left: 15px;
          cursor: pointer;
          color: inherit;
          padding: 0;
          line-height: 1;
        }

        /* Dialogo di conferma */
        .confirm-dialog-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 2000;
        }

        .confirm-dialog {
          background: white;
          border-radius: 10px;
          padding: 30px;
          max-width: 500px;
          width: 90%;
          box-shadow: 0 5px 30px rgba(0, 0, 0, 0.3);
        }

        .confirm-dialog h4 {
          margin: 0 0 20px 0;
          color: #2c3e50;
        }

        .confirm-dialog p {
          margin: 10px 0;
          color: #666;
        }

        .warning-text {
          background: #fff3cd;
          border: 1px solid #ffeeba;
          color: #856404;
          padding: 10px;
          border-radius: 5px;
          margin-top: 15px;
        }

        .dialog-buttons {
          display: flex;
          gap: 10px;
          justify-content: flex-end;
          margin-top: 25px;
        }

        /* Loading e errori */
        .loading, .error-message, .no-results {
          text-align: center;
          padding: 40px;
          color: #666;
        }

        .error-message {
          color: #dc3545;
          background: #f8d7da;
          border: 1px solid #f5c6cb;
          border-radius: 5px;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .users-grid {
            grid-template-columns: 1fr;
          }

          .controls-bar {
            flex-direction: column;
            align-items: stretch;
          }

          .search-section, .filters-section, .stats-section {
            width: 100%;
          }

          .confirm-dialog {
            width: 95%;
            padding: 20px;
          }
        }

        @media (max-width: 480px) {
          .admin-qr-management {
            padding: 10px;
          }

          .user-qr-card {
            padding: 15px;
          }

          .btn-sm {
            font-size: 11px;
            padding: 5px 8px;
          }

          .qr-actions, .account-actions {
            flex-direction: column;
          }

          .btn-account {
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

export default AdminQRManagement;